'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { Card } from '@/entities/auth'
import { SignUpHostButton, useSignUpHostStore } from '@/features/sign-up'
import { THostPayForm, ZHostPayForm } from '@/features/sign-up'
import { AccountVerificationProvider } from '@/features/update-account'
import { createReceiptInformationValidation } from '@/features/update-receipt-information'
import { Slot, useDebounce } from '@/shared/lib'
import { Form } from '@/shared/ui'

import { HostPayAccount } from './host-pay-account'
import { ReceiptForFee } from './receipt-for-fee'
import { RecommenderPhoneNumber } from './recommender-phone-number'

export const SignUpHostPay = () => {
  const tDepositInfo = useTranslations('deposit_information')
  const tReceiptInfo = useTranslations('receipt_information')

  const t = useTranslations('signup_bank_account')

  const { actions, pay } = useSignUpHostStore()

  const schema = useMemo(
    () =>
      ZHostPayForm.superRefine((data, ctx) => {
        if (!data.bankName) {
          ctx.addIssue({
            path: ['bankName'],
            code: z.ZodIssueCode.custom,
            message: tDepositInfo('error.settlement_bank_required'),
          })
        }
        if (!data.bankAccount) {
          ctx.addIssue({
            path: ['bankAccount'],
            code: z.ZodIssueCode.custom,
            message: tDepositInfo('error.settlement_account_number_required'),
          })
        }
      }).superRefine(createReceiptInformationValidation(tReceiptInfo)),
    [tDepositInfo, tReceiptInfo],
  )

  const form = useForm<THostPayForm>({
    defaultValues: pay,
    resolver: zodResolver(schema),
    reValidateMode: 'onSubmit',
  })

  const watch = useWatch({
    control: form.control,
  })

  const debouncedWatch = useDebounce(watch, 300)

  useEffect(() => {
    actions.updatePay(debouncedWatch as THostPayForm)
  }, [debouncedWatch, actions])

  return (
    <Form.Root form={form}>
      <AccountVerificationProvider>
        <Card progress={75}>
          <Slot name="title">{t('title')}</Slot>
          <Slot name="children">
            <HostPayAccount />
            <ReceiptForFee />
          </Slot>
          <Slot name="subContent">
            <RecommenderPhoneNumber />
            <SignUpHostButton />
          </Slot>
        </Card>
      </AccountVerificationProvider>
    </Form.Root>
  )
}
